import { ChangeEvent, useEffect, useId, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@lib/components/Icon'

import {
  SEARCH_CLEAR_ICON_PATH,
  SEARCH_DEFAULT_DEBOUNCE,
  SEARCH_ICON_PATH,
  SEARCH_PLACEHOLDER_KEY,
} from './constants'
import styles from './styles.module.scss'
import { SearchProps } from './types'

export const Search = ({
  value,
  defaultValue = '',
  placeholder,
  ariaLabel,
  onDebouncedChange,
  onChange,
  debounce = SEARCH_DEFAULT_DEBOUNCE,
  iconPath = SEARCH_ICON_PATH,
  clearIconPath = SEARCH_CLEAR_ICON_PATH,
  showClearButton = true,
  onClear,
}: SearchProps) => {
  const { t } = useTranslation()
  const translatedPlaceholder = t(placeholder ?? SEARCH_PLACEHOLDER_KEY)
  const resolvedAriaLabel = ariaLabel ?? translatedPlaceholder
  const resolvedIconAlt = translatedPlaceholder
  const [searchValue, setSearchValue] = useState(value ?? defaultValue)
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (value !== undefined) {
      setSearchValue(value)
    }
  }, [value])

  useEffect(() => {
    if (!onDebouncedChange) {
      return
    }
    window.clearTimeout(debounceTimeoutRef.current)
    const timeoutId = window.setTimeout(() => {
      onDebouncedChange(searchValue)
    }, debounce)
    debounceTimeoutRef.current = timeoutId

    return () => {
      window.clearTimeout(timeoutId)
      debounceTimeoutRef.current = undefined
    }
  }, [searchValue, debounce, onDebouncedChange])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    setSearchValue(nextValue)
    onChange?.(nextValue)
  }

  const handleClear = () => {
    if (!searchValue) {
      return
    }

    window.clearTimeout(debounceTimeoutRef.current)
    debounceTimeoutRef.current = undefined
    setSearchValue('')
    onChange?.('')
    if (onDebouncedChange) {
      onDebouncedChange('')
    }
    onClear?.()
    inputRef.current?.focus()
  }

  return (
    <label className={styles.search} htmlFor={inputId}>
      <span className={styles.icon} aria-hidden="true">
        <Icon path={iconPath} alt={resolvedIconAlt} className={styles.iconImage} />
      </span>
      <input
        id={inputId}
        type="search"
        value={searchValue}
        onChange={handleChange}
        placeholder={translatedPlaceholder}
        aria-label={resolvedAriaLabel}
        ref={inputRef}
        className={styles.input}
      />
      {showClearButton && Boolean(searchValue) && (
        <button
          type="button"
          onClick={handleClear}
          className={styles.clearButton}
        >
          <Icon
            path={clearIconPath}
            className={styles.iconImage}
          />
        </button>
      )}
    </label>
  )
}
